import { useRef, useState, type PointerEvent } from "react";
import { haptic } from "../telegram";

/**
 * Wordscapes-style letter wheel: drag through letters to build a word,
 * release to submit. Each letter node is usable once per swipe; sliding
 * back onto the previous node un-selects. Swipe-first, works with a mouse.
 */
const S = 270;
const C = S / 2;
const RING = 92;
const NODE = 27;
const HIT = 38;

interface Props {
  letters: string[];
  disabled: boolean;
  minLen: number;
  onSubmit: (word: string) => void;
}

export function LetterWheel({ letters, disabled, minLen, onSubmit }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [path, setPath] = useState<number[]>([]);
  const [cursor, setCursor] = useState<{ x: number; y: number } | null>(null);
  const dragging = useRef(false);

  const nodes = letters.map((ch, i) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / letters.length;
    return { ch, x: C + RING * Math.cos(a), y: C + RING * Math.sin(a) };
  });

  const toLocal = (e: PointerEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return { x: ((e.clientX - rect.left) * S) / rect.width, y: ((e.clientY - rect.top) * S) / rect.height };
  };

  const hitNode = (p: { x: number; y: number }): number => {
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i] as { x: number; y: number };
      if ((p.x - n.x) ** 2 + (p.y - n.y) ** 2 <= HIT * HIT) return i;
    }
    return -1;
  };

  const down = (e: PointerEvent) => {
    if (disabled) return;
    const p = toLocal(e);
    if (!p) return;
    const i = hitNode(p);
    if (i < 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    haptic.tap();
    setPath([i]);
    setCursor(p);
  };

  const move = (e: PointerEvent) => {
    if (!dragging.current) return;
    const p = toLocal(e);
    if (!p) return;
    setCursor(p);
    const i = hitNode(p);
    if (i < 0) return;
    setPath((prev) => {
      if (prev[prev.length - 1] === i) return prev;
      if (prev.length > 1 && prev[prev.length - 2] === i) {
        haptic.tap();
        return prev.slice(0, -1); // slide back = undo
      }
      if (prev.includes(i)) return prev;
      haptic.tap();
      return [...prev, i];
    });
  };

  const up = () => {
    if (!dragging.current) return;
    dragging.current = false;
    setCursor(null);
    setPath((prev) => {
      const word = prev.map((i) => letters[i]).join("");
      if (word.length >= minLen) onSubmit(word);
      return [];
    });
  };

  const word = path.map((i) => letters[i]).join("");
  const line = path.map((i) => nodes[i] as { x: number; y: number });

  return (
    <div className="select-none">
      <div className="h-8 text-center text-xl font-extrabold uppercase tracking-[0.2em]">{word || " "}</div>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${S} ${S}`}
        className="mx-auto block w-[min(68vw,270px)]"
        style={{ touchAction: "none" }}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
      >
        <circle cx={C} cy={C} r={C - 2} fill="rgba(128,128,128,0.12)" />
        {line.length > 0 && (
          <polyline
            points={[...line.map((n) => `${n.x},${n.y}`), cursor ? `${cursor.x},${cursor.y}` : ""].join(" ")}
            fill="none"
            stroke="var(--tg-theme-button-color, #2ea6ff)"
            strokeWidth={7}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.85}
          />
        )}
        {nodes.map((n, i) => {
          const active = path.includes(i);
          return (
            <g key={i}>
              <circle
                cx={n.x}
                cy={n.y}
                r={NODE}
                // rgba, not the secondary-bg var — pure-black themes render that ≈ #000
                fill={active ? "var(--tg-theme-button-color, #2ea6ff)" : "rgba(128,128,128,0.28)"}
              />
              <text
                x={n.x}
                y={n.y + 1}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={22}
                fontWeight={800}
                fill={active ? "var(--tg-theme-button-text-color, #fff)" : "var(--tg-theme-text-color, #fff)"}
              >
                {n.ch.toUpperCase()}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
